import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Paper,
  Typography } from
'@mui/material';
import { DeduplicationTask } from 'crqlar-react-common/dist/models/profile/deduplicationTask.model';
import { formatDuration, formatNumber } from './deduplicationUtils';

interface SimilarityPhaseCardProps {
  task: DeduplicationTask;
}

const SimilarityPhaseCard: React.FC<SimilarityPhaseCardProps> = ({ task }) => {
  const { t } = useTranslation();
  const stats = task.similarityStats;
  if (!stats) return null;

  const total = stats.totalMatches || 0;
  const categories = [
  { label: 'Definite Duplicates', value: stats.definiteDuplicates || 0, color: '#d32f2f' },
  { label: 'Probable Duplicates', value: stats.probableDuplicates || 0, color: '#f57c00' },
  { label: 'Possible Duplicates', value: stats.possibleDuplicates || 0, color: '#fbc02d' },
  { label: 'Likely Family', value: stats.likelyFamily || 0, color: '#1976d2' }];


  return (
    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
            <CardContent>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>{t("Step 2: Similarity Scoring")}

        </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>{t("Duration:")}
          {formatDuration(stats.durationMs)}
                </Typography>

                <Paper elevation={0} sx={{ bgcolor: 'background.default', mb: 4 }}>
                    <Typography variant="h6" color="primary" gutterBottom>{t("Scoring Performance")}

          </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatNumber(stats.candidatePairsScored)}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">{t("Pairs scored (")}
            {formatNumber(Math.floor((stats.candidatePairsScored || 0) / ((stats.durationMs || 1) / 1000)))} {t("pairs/sec)")}
          </Typography>
                </Paper>

                <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3, fontWeight: 600 }}>{t("Duplicate Categories Distribution")}

        </Typography>
                {categories.map((cat) => {
          const percentage = total > 0 ? cat.value / total * 100 : 0;
          return (
            <Box key={cat.label} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>{cat.label}</Typography>
                                <Typography variant="h6" color="text.secondary">
                                    {formatNumber(cat.value)} ({percentage.toFixed(1)}%)
                                </Typography>
                            </Box>
                            <LinearProgress
                variant="determinate"
                value={Math.min(percentage, 100)}
                sx={{
                  height: 12,
                  borderRadius: 2,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: cat.color
                  }
                }} />

                        </Box>);

        })}
                <Typography variant="h4" sx={{ mt: 4, fontWeight: 600 }}>{t("Total Matches:")}
          {formatNumber(total)}
                </Typography>
            </CardContent>
        </Card>);

};

export default SimilarityPhaseCard;
